import React, { CSSProperties, useRef, useState } from 'react'
import { COLORS, SPACE } from '../../theme'
import Space from './Space'
import View from './View'
import Text from './TextNew'
import CloseXSvg from '../other/svg/CloseXSvg'
import TickSvg from '../other/svg/TickSvg'

export interface SelectOption {
  optionVal: string | number | boolean
  optionLabel: string
  optionIcon?: () => JSX.Element
  dataTestId?: string
}
interface Props {
  value: string | number | boolean
  onChange: (val: string | number | boolean) => void
  label: string
  options: SelectOption[]
  onSelectOpen?: () => void
  onSelectClose?: () => void
  dataTestId?: string
  containerStyles?: CSSProperties
  actionButtonsStyles?: CSSProperties
  closeButtonStyles?: CSSProperties
  optionsContainerStyles?: CSSProperties
  chosenOptionStyles?: CSSProperties
  chosenOptionContainerStyles?: CSSProperties
  placeholderStyles?: CSSProperties
  error?: boolean
  labelStyles?: CSSProperties
}

const SelectInput = ({
  value,
  onChange,
  label,
  options,
  onSelectOpen = () => null,
  onSelectClose = () => null,
  dataTestId,
  error = false,
  containerStyles: containerStylesProps = {},
  actionButtonsStyles: actionButtonsStylesProps = {},
  optionsContainerStyles: optionsContainerStylesProps = {},
  chosenOptionStyles: chosenOptionStylesProps = {},
  chosenOptionContainerStyles: chosenOptionContainerStylesProps = {},
  closeButtonStyles: closeButtonStylesProps = {},
  labelStyles: labelStylesProps = {},
  placeholderStyles: placeholderStylesProps = {}
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const inputOpen = value.toString().length > 0 || isOpen
  const ChoseOptionIcon = options.find((item) => item.optionVal === value)?.optionIcon
  const containerRef = useRef<HTMLDivElement>(null)
  const containerStyles = `border-4 rounded-[18px] h-13 cursor-pointer relative bg-white ${
    error ? '!border-errorRed !border-2' : 'border-[#74a0d5]'
  }`

  const optionsContainerStyles =
    'max-h-[200px] w-[242px] bg-white  overflow-y-scroll border-t-0 border-[0.2px] border-groupBlue relative left-1 rounded-2 -top-12 pt-10'
  const optionsContainerWidth =
    containerRef.current === null ? '240px' : containerRef.current.getBoundingClientRect().width - SPACE * 2

  return (
    <>
      <div
        ref={containerRef}
        className={containerStyles}
        style={{ borderColor: inputOpen ? '#74a0d5' : COLORS.groupBlue, ...containerStylesProps }}
        onClick={() => {
          setIsOpen(!isOpen)
          onSelectOpen()
        }}
        data-testid={dataTestId}>
        <View
          className="border-[#4982C8] w-full"
          style={{
            borderBottomLeftRadius: isOpen ? 0 : 16,
            borderBottomRightRadius: isOpen ? 0 : 16
          }}>
          {value.toString().length > 0 && (
            <View>
              <Text style={{ zIndex: isOpen ? 20 : 0, ...labelStylesProps }} className="text-grey relative top-1 px-2">
                {label}
              </Text>
            </View>
          )}
          <View
            className="flex px-3 text-xl text-darkGrey rounded-4 flex-1 justify-between relative w-full"
            style={{
              paddingTop: inputOpen ? SPACE : 0,
              zIndex: isOpen ? 30 : 0,
              background: isOpen ? COLORS.white : 'unset',
              ...chosenOptionContainerStylesProps
            }}>
            <View className="flex items-center">
              {typeof ChoseOptionIcon !== 'undefined' && (
                <>
                  <ChoseOptionIcon />
                  <Space x={2} />
                </>
              )}
              {value.toString().length > 0 && (
                <Text className="text-[14px] font-Medium text-darkGrey" style={chosenOptionStylesProps}>
                  {options.find((item) => item.optionVal === value)?.optionLabel}
                </Text>
              )}
              {value.toString().length === 0 && (
                <Text style={placeholderStylesProps} className="text-[14px] font-Medium text-darkGrey pt-3">
                  {label}
                </Text>
              )}
            </View>
            <View className="" style={actionButtonsStylesProps}>
              {value.toString().length > 0 ? (
                <CloseXSvg
                  style={closeButtonStylesProps}
                  className="flex justify-center items-center border w-5 h-5 rounded-4 text-[#3682ce] relative -top-3 cursor-pointer"
                  svgStyles="w-2 h-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (value.toString().length > 0) {
                      onChange('')
                    }
                    setIsOpen(false)
                  }}
                />
              ) : (
                <TickSvg className="flex justify-center items-center w-5 h-5 text-[#3682ce] relative cursor-pointer top-3" />
              )}
            </View>
          </View>
        </View>
      </div>
      {isOpen && (
        <View className="absolute z-20">
          <View
            style={{ width: optionsContainerWidth, ...optionsContainerStylesProps }}
            className={optionsContainerStyles}>
            {options.map((option) => {
              const Icon = typeof option.optionIcon === 'undefined' ? <></> : option.optionIcon()

              return (
                <View
                  className="flex p-2 hover:bg-lightGrey items-center cursor-pointer bg-white"
                  onClick={() => {
                    setIsOpen(false)
                    onChange(option.optionVal)
                    onSelectClose()
                  }}
                  data-testid={option.dataTestId}
                  key={option.optionLabel}>
                  {Icon}
                  <Space x={1} />
                  <Text className="text-l text-groupBlue">{option.optionLabel}</Text>
                </View>
              )
            })}
          </View>
        </View>
      )}
      {isOpen && (
        <View
          onClick={() => {
            setIsOpen(false)
            onSelectClose()
          }}
          className="h-[3000px] w-[3000px] -left-[1000px] -top-[1000px] z-10 absolute"
        />
      )}
    </>
  )
}

export default SelectInput
